import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlass, Plus, ShoppingCart, Trash } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import InventoryFilters from '../components/InventoryFilters';
import Pagination from '../components/Pagination';
import CompareToggle from '../components/CompareToggle';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared storage key with RFQ form
const RFQ_STORAGE_KEY = 'rfq_parts_v7';

const saveParts = (parts) => {
  try { localStorage.setItem(RFQ_STORAGE_KEY, JSON.stringify(parts)); } catch(e) {}
};
const loadParts = () => {
  try { 
    const s = localStorage.getItem(RFQ_STORAGE_KEY);
    return s ? JSON.parse(s) : [];
  } catch(e) { return []; }
};

const PAGE_SIZE = 50;
const FILTER_KEYS = ['type', 'cap', 'speed', 'ff', 'rank', 'volt', 'die', 'org', 'cond'];

// Map legacy configurator params (?size=64GB&type=DDR5&speed=6400&capacity=RDIMM) to spec filters.
const migrateLegacy = (sp) => {
  if (!sp.has('size') && !sp.has('capacity')) return null;
  const next = new URLSearchParams(sp);
  const size = sp.get('size');
  if (size) next.set('cap', size.replace(/GB/i, ''));
  const ff = sp.get('capacity');
  if (ff && ff !== 'none') next.set('ff', ff.replace('3DS ', ''));
  next.delete('size'); next.delete('capacity');
  return next;
};

const InventoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventory, setInventory] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [quantities, setQuantities] = useState({});
  const page = Math.max(1, parseInt(searchParams.get('page')) || 1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  
  // RFQ Cart state
  const [rfqParts, setRfqParts] = useState(() => loadParts());

  // Sync rfqParts to localStorage
  useEffect(() => {
    saveParts(rfqParts);
  }, [rfqParts]);


  useEffect(() => {
    const migrated = migrateLegacy(searchParams);
    if (migrated) { setSearchParams(migrated, { replace: true }); return; }
    loadInventory();
    const fp = new URLSearchParams();
    FILTER_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) fp.append(k === 'cond' ? 'condition' : k, v); });
    axios.get(`${API_URL}/api/inventory/facets?${fp.toString()}`).then((r) => setFacets(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!('page' in changes)) next.delete('page');
    setSearchParams(next);
  };

  const hrefFor = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p > 1) next.set('page', p); else next.delete('page');
    const qs = next.toString();
    return `/inventory${qs ? `?${qs}` : ''}`;
  };

  const loadInventory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const q = searchParams.get('q');
      if (q) params.append('search', q);
      FILTER_KEYS.forEach((k) => {
        const v = searchParams.get(k);
        if (v) params.append(k === 'cond' ? 'condition' : k, v);
      });
      params.append('limit', PAGE_SIZE);
      params.append('skip', (page - 1) * PAGE_SIZE);
      const response = await axios.get(`${API_URL}/api/inventory?${params.toString()}`);
      setInventory(response.data.items || []);
      setTotal(response.data.total || 0);
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    updateParams({ q: search.trim() });
  };

  const handleQuantityChange = (itemName, value) => {
    setQuantities({ ...quantities, [itemName]: parseInt(value) || 1 });
  };

  // ADD TO RFQ handler
  const handleAddToRfq = (item) => {
    const qty = quantities[item.name] || 1; // Default to 1 if empty
    
    // Check if already in RFQ
    if (rfqParts.find(p => p.name === item.name)) {
      alert(`${item.name} is already in your RFQ!`);
      toast.error('Part already in RFQ');
      return;
    }

    const newPart = {
      name: item.name,
      description: item.description,
      condition: item.condition,
      taa: item.taa,
      qty_on_hand: item.qty_on_hand,
      requested_qty: qty,
      available_qty_at_selection: item.qty_on_hand,
      selected_at: new Date().toISOString()
    };

    const newList = [...rfqParts, newPart];
    setRfqParts(newList);
    saveParts(newList);
    
    alert(`ADDED TO RFQ!\n\nPart: ${item.name}\nQuantity: ${qty}\n\nYour RFQ now has ${newList.length} part(s).`);
    toast.success(`Added ${item.name} x${qty} to RFQ`);
  };

  // Remove from RFQ
  const handleRemoveFromRfq = (partName) => {
    const updated = rfqParts.filter(p => p.name !== partName);
    setRfqParts(updated);
    saveParts(updated);
    toast.info('Part removed from RFQ');
  };

  // Check if item is in RFQ
  const isInRfq = (itemName) => rfqParts.some(p => p.name === itemName);

  const totalRfqQty = rfqParts.reduce((sum, p) => sum + (p.requested_qty || 0), 0);

  return (
    <div className="min-h-screen">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* RFQ Cart Banner */}
        <div className="mb-6 p-4 bg-[#10B981] rounded-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <ShoppingCart size={28} className="text-white" weight="fill" />
              <span className="text-white font-mono font-bold text-xl">
                YOUR RFQ: {rfqParts.length} PART(S) | {totalRfqQty} TOTAL QTY
              </span>
            </div>
            {rfqParts.length > 0 && (
              <Button 
                onClick={() => window.location.href = '/rfq'}
                className="bg-white text-[#10B981] hover:bg-gray-100 font-bold"
              >
                GO TO RFQ FORM →
              </Button>
            )}
          </div>
          {rfqParts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {rfqParts.map((p, i) => (
                <div key={i} className="bg-white/20 rounded px-3 py-1 flex items-center gap-2">
                  <span className="text-white text-sm">{p.name} x{p.requested_qty}</span>
                  <button 
                    onClick={() => handleRemoveFromRfq(p.name)}
                    className="text-white hover:text-red-200"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <h1 className="text-4xl font-['JetBrains_Mono'] font-bold mb-8">Live Inventory</h1>

        {/* Search and Filters */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <Input
                placeholder="Search by part number"
                data-testid="inventory-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-transparent border-[#334155] h-12 text-lg"
              />
            </div>
            <div>
              <Button 
                onClick={handleSearch} 
                data-testid="inventory-search-btn"
                className="w-full h-12 bg-[#0047AB] hover:bg-[#005DF0] font-mono text-lg"
              >
                <MagnifyingGlass className="mr-2" size={20} />
                Search
              </Button>
            </div>
          </div>
          <InventoryFilters
            facets={facets}
            values={Object.fromEntries(FILTER_KEYS.map((k) => [k, searchParams.get(k) || '']))}
            onChange={(k, v) => updateParams({ [k]: v })}
            onClear={() => { setSearch(''); setSearchParams(new URLSearchParams()); }}
          />
        </div>

        <div className="flex items-center justify-between mb-3 text-xs font-mono text-[#94A3B8]" data-testid="inventory-result-count">
          <span>{total.toLocaleString()} modules{total > 0 && ` · showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`}</span>
          <span>Page {page} of {totalPages}</span>
        </div>

        {/* Inventory Results */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-[#94A3B8]">
              <p className="font-mono">Loading inventory...</p>
            </div>
          ) : inventory.length === 0 ? (
            <div className="p-12 text-center text-[#94A3B8]">
              <p className="font-mono">No inventory found.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1E293B]">
              {inventory.map((item, index) => {
                const inRfq = isInRfq(item.name);
                return (
                  <div 
                    key={item.id || index} 
                    className={`p-4 ${inRfq ? 'bg-[#10B981]/10' : 'hover:bg-[#1E293B]/30'} transition-colors`}
                  >
                    {/* Part Info */}
                    <div className="mb-3">
                      <Link
                        to={`/product/${item.slug || item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                        className="font-mono text-cyan-400 hover:text-cyan-300 font-bold text-lg underline-offset-4 hover:underline"
                        data-testid={`product-link-${item.name}`}
                      >
                        {item.name}
                      </Link>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-[#94A3B8] text-sm mt-1">
                          {item.description}
                        </div>
                        <CompareToggle item={item} testId={`compare-toggle-${item.slug}`} />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-[#94A3B8]">On Hand: </span>
                          <span className="font-mono text-[#10B981] font-bold">{item.qty_on_hand}</span>
                        </div>
                        <div>
                          <span className="text-[#94A3B8]">Incoming: </span>
                          <span className="font-mono text-[#F59E0B]">{item.qty_incoming}</span>
                        </div>
                        <div>
                          <span className="text-[#94A3B8]">Condition: </span>
                          <span className="font-mono text-[#007AFF]">{item.condition}</span>
                        </div>
                        <div>
                          <span className="text-[#94A3B8]">TAA: </span>
                          <span className={`font-mono ${item.taa === 'Yes' ? 'text-[#10B981]' : 'text-[#94A3B8]'}`}>
                            {item.taa}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* QTY Input + ADD Button Row */}
                    <div className="flex items-center gap-3 pt-3 border-t border-[#334155]">
                      <label className="text-white font-bold whitespace-nowrap">QTY:</label>
                      <Input
                        type="number"
                        min="1"
                        value={quantities[item.name] || ''}
                        onChange={(e) => handleQuantityChange(item.name, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="1"
                        className="bg-[#05050A] border-[#334155] w-24 h-12 text-center text-xl font-bold placeholder:text-[#666]"
                        disabled={inRfq}
                      />
                      
                      {inRfq ? (
                        <Button
                          onClick={() => handleRemoveFromRfq(item.name)}
                          className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 h-12 flex-1 text-lg"
                        >
                          <Trash size={20} className="mr-2" />
                          REMOVE FROM RFQ
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleAddToRfq(item)}
                          className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-6 h-12 flex-1 text-lg"
                        >
                          <Plus size={24} weight="bold" className="mr-2" />
                          ADD TO RFQ
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Pagination page={page} totalPages={totalPages} hrefFor={hrefFor} />

        {/* Bottom CTA */}
        {rfqParts.length > 0 && (
          <div className="mt-8 p-6 bg-[#10B981] rounded-lg text-center">
            <p className="text-white font-bold text-xl mb-4">
              You have {rfqParts.length} part(s) in your RFQ
            </p>
            <Button 
              onClick={() => window.location.href = '/rfq'}
              className="bg-white text-[#10B981] hover:bg-gray-100 font-bold text-xl px-12 py-4 h-auto"
            >
              CONTINUE TO RFQ FORM →
            </Button>
          </div>
        )}

        {rfqParts.length === 0 && (
          <div className="mt-8 text-center">
            <p className="text-[#94A3B8] mb-4">
              Enter quantity and click ADD TO RFQ for the parts you need.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default InventoryPage;
